import { useCallback, useEffect, useMemo, useState } from 'react'
import { useGetTopicDetails } from '@/hooks/api/dashboard'
import { useLocalStorage } from '@/hooks/common'
import { Topic } from '@/types'
import { addUploadAssets } from '@/pages/pattern/renderer/main'
import { topicToAlbumDetail } from '@/features/album/mappers'
import { AlbumDetail, AlbumPhoto } from '@/features/album/types'
import { parseSourceAlbumIds } from '../utils/galleryXData'

export interface SourceAlbumRef {
  id: string
  title: string
  coverUrl?: string
  photoCount: number
}

export interface SourceAlbumPhoto extends AlbumPhoto {
  albumId: string
  albumTitle: string
}

/**
 * Loads every album linked on a GALLERY topic (`sourceAlbumId` +
 * `sourceAlbumIds` in topicXData) and flattens their photos for Studio Assets.
 * Back-compat: a lone `sourceAlbumId` still works.
 */
export function useGallerySourceAlbum(topic: Topic | null) {
  const getDetails = useGetTopicDetails()
  const [ewUser] = useLocalStorage('ewUser', null)

  const sourceAlbumIds = useMemo(() => parseSourceAlbumIds(topic?.topicXData), [topic?.topicXData])
  const sourceAlbumId = sourceAlbumIds[0]

  const [albumsById, setAlbumsById] = useState<Record<string, AlbumDetail>>({})
  const [loading, setLoading] = useState(false)
  /** Bumped by `refresh()` so uploads re-fetch without changing linked ids. */
  const [reloadTick, setReloadTick] = useState(0)
  const idsKey = sourceAlbumIds.join(',')

  const loadAlbums = useCallback(
    async (ids: string[], cancelled?: () => boolean) => {
      if (ids.length === 0) {
        if (!cancelled?.()) {
          setAlbumsById({})
          setLoading(false)
        }
        return
      }
      setLoading(true)
      const results = await Promise.all(
        ids.map(async (id) => {
          try {
            const topicData = await getDetails.mutateAsync({ topicID: id, pageNo: 1 })
            return topicToAlbumDetail(topicData, String(ewUser?.userID) === String(topicData.userID))
          } catch {
            return null
          }
        }),
      )
      if (cancelled?.()) return
      const next: Record<string, AlbumDetail> = {}
      for (const album of results) {
        if (album) next[album.id] = album
      }
      setAlbumsById(next)
      setLoading(false)
    },
    [getDetails, ewUser?.userID],
  )

  useEffect(() => {
    let cancelled = false
    void loadAlbums(sourceAlbumIds, () => cancelled)
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, ewUser?.userID, reloadTick])

  const refresh = useCallback(() => {
    setReloadTick((t) => t + 1)
  }, [])

  const linkedAlbums: SourceAlbumRef[] = useMemo(
    () =>
      sourceAlbumIds.map((id) => {
        const a = albumsById[id]
        return {
          id,
          title: a?.title || 'Album',
          coverUrl: a?.coverUrl,
          photoCount: a?.photoCount ?? a?.photos?.length ?? 0,
        }
      }),
    [sourceAlbumIds, albumsById],
  )

  const photos: SourceAlbumPhoto[] = useMemo(() => {
    const out: SourceAlbumPhoto[] = []
    const seen = new Set<string>()
    for (const id of sourceAlbumIds) {
      const album = albumsById[id]
      if (!album) continue
      for (const p of album.photos || []) {
        const key = p.egoId || p.url || p.id
        if (!key || seen.has(key)) continue
        seen.add(key)
        out.push({ ...p, albumId: id, albumTitle: album.title })
      }
    }
    return out
  }, [sourceAlbumIds, albumsById])

  useEffect(() => {
    if (photos.length === 0) return
    addUploadAssets(
      photos
        .filter((p) => p.egoId)
        .map((p) => ({
          egoID: p.egoId,
          egoFileURL: p.thumbUrl || p.url,
          egoImageFullURL: p.url || p.thumbUrl,
        })),
    )
  }, [photos])

  const album = sourceAlbumId ? albumsById[sourceAlbumId] ?? null : null

  return {
    sourceAlbumId,
    sourceAlbumIds,
    linkedAlbums,
    album,
    photos,
    loading: sourceAlbumIds.length > 0 && loading,
    refresh,
  }
}
